import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Table, Tag, Button, Card, Select, Space, Typography,
  Popover, Tooltip, message, Spin, Alert, Statistic, Row, Col, Progress,
} from 'antd';
import {
  SyncOutlined, DownloadOutlined, FilePdfOutlined,
  ExclamationCircleOutlined, CheckCircleOutlined, WarningOutlined,
  BellOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation } from '@tanstack/react-query';

const { Title, Text } = Typography;

// ─── API helpers ──────────────────────────────────────────────────────────────
// CE scan calls are routed through the CSG Gateway, which proxies to the Agent Backend.
// This avoids direct browser→Agent Backend calls (no DNS for agent.odin1.net in prod).
// Auth: MSAL Bearer token + CF Access service token (pass through to gateway).
import { getAgentAuthHeaders } from '../../services/api/settingsApi';
const AGENT_URL = import.meta.env.VITE_CSG_GATEWAY_URL || 'http://localhost:3000';
const getAuthHeaders = getAgentAuthHeaders;

async function fetchCeResults(status, upsellOnly) {
  const params = new URLSearchParams();
  if (status && status !== 'ALL') params.set('status', status);
  if (upsellOnly) params.set('upsell', 'true');

  const qs = params.toString();
  const url = `${AGENT_URL}/ce-scan/results${qs ? `?${qs}` : ''}`;

  const response = await fetch(url, { headers: getAuthHeaders() });

  if (!response.ok) {
    const err = new Error(`CE scan results request failed (${response.status})`);
    err.response = { status: response.status, data: await response.json().catch(() => ({})) };
    throw err;
  }

  return response.json();
}

async function triggerScan() {
  const response = await fetch(`${AGENT_URL}/ce-scan/trigger`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const err = new Error(`Trigger scan failed (${response.status})`);
    err.response = { status: response.status, data };
    throw err;
  }

  return response.json();
}

async function fetchActiveRun() {
  const response = await fetch(`${AGENT_URL}/ce-scan/runs/active`, {
    headers: getAuthHeaders(),
  });
  if (response.status === 404) return null;
  if (!response.ok) return null; // silently ignore transient errors
  return response.json();
}

// ─── Status badge ─────────────────────────────────────────────────────────────
const STATUS_COLOURS = {
  'AT RISK': 'error',
  'PARTIAL': 'warning',
  'CE READY': 'success',
  'SCAN_ERROR': 'default',
};

const STATUS_ICONS = {
  'AT RISK': <ExclamationCircleOutlined />,
  'PARTIAL': <WarningOutlined />,
  'CE READY': <CheckCircleOutlined />,
};

function StatusBadge({ status }) {
  return (
    <Tag color={STATUS_COLOURS[status] || 'default'} icon={STATUS_ICONS[status]}>
      {status}
    </Tag>
  );
}

// ─── Control tick/cross/dash ──────────────────────────────────────────────────
function Control({ value }) {
  if (value === null || value === undefined) return <Text type="secondary">—</Text>;
  return value
    ? <Text style={{ color: 'var(--odin-success)' }}>✓</Text>
    : <Text type="danger">✗</Text>;
}

// ─── Percentage control (pass/fail with numeric label) ────────────────────────
function PercentControl({ value, threshold = 80 }) {
  if (value === null || value === undefined) return <Text type="secondary">—</Text>;
  const pass = value >= threshold;
  return (
    <span>
      {pass
        ? <Text style={{ color: 'var(--odin-success)' }}>✓</Text>
        : <Text type="danger">✗</Text>}
      <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>{value}%</Text>
    </span>
  );
}

// ─── Encryption (is it enabled, and over how much of the estate) ─────────────
// Encryption is not one of the five Cyber Essentials controls, so this cell reports
// whether encryption is ENABLED rather than whether it clears a bar. A partial rollout
// and thin visibility are information; the only failing state is machines reporting and
// none of them encrypted.
//
// The figure merges Sophos Central Device Encryption and Intune device by device, because
// each is blind to encryption the other manages — reading Intune alone is how a fully
// Sophos-encrypted customer spent a month on this report showing 0%.
export function EncryptionControl({ record }) {
  const pct = record?.drive_encryption_pct;
  const coverage = record?.encryption_coverage_pct;
  const reporting = record?.encryption_reporting_count;
  const workstations = record?.encryption_workstation_count;
  const source = record?.encryption_source;

  if (pct === null || pct === undefined) {
    const title =
      coverage === 0 && workstations
        ? `No encryption state reported by any of ${workstations} workstations`
        : 'Not measured — no encryption source answered for this customer';
    return (
      <Text type="secondary" title={title}>
        —
      </Text>
    );
  }

  // Nothing encrypted among machines that did report: the one real finding.
  const noneEncrypted = pct === 0 && (reporting ?? 0) > 0;
  const sourceLabel =
    source === 'sophos+intune'
      ? 'Sophos + Intune'
      : source === 'sophos'
        ? 'Sophos'
        : source === 'intune'
          ? 'Intune'
          : null;

  return (
    <span title={sourceLabel ? `Source: ${sourceLabel}` : undefined}>
      {noneEncrypted ? (
        <Text type="danger">✗</Text>
      ) : (
        <Text style={{ color: 'var(--odin-success)' }}>✓</Text>
      )}
      <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>
        {pct}%
      </Text>
      {reporting !== null && reporting !== undefined && workstations ? (
        <Text
          type="secondary"
          style={{ fontSize: 10, marginLeft: 6 }}
          title={`Encryption state reported by ${reporting} of ${workstations} workstations`}
        >
          {reporting}/{workstations} seen
        </Text>
      ) : null}
    </span>
  );
}

// ─── Cooldown helpers ─────────────────────────────────────────────────────────
const COOLDOWN_MS = 30 * 60 * 1000;

function isCoolingDown(lastScanAt) {
  if (!lastScanAt) return false;
  return Date.now() - new Date(lastScanAt).getTime() < COOLDOWN_MS;
}

// Safe parser for gap_detail — handles both JS arrays (from API) and JSON strings (from DB)
function parseArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try { return JSON.parse(value); } catch { return []; }
  }
  return [];
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function CyberEssentialsReport() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('Admin');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [upsellOnly, setUpsellOnly] = useState(false);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['ce-scan-results', statusFilter, upsellOnly],
    queryFn: () => fetchCeResults(statusFilter, upsellOnly),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Poll for active scan — fast when running, slow otherwise (catches nightly scan too)
  const { data: activeRun, refetch: refetchActive } = useQuery({
    queryKey: ['ce-scan-active'],
    queryFn: fetchActiveRun,
    refetchInterval: (query) => (query.state.data?.id ? 5000 : 60000),
    staleTime: 0,
    initialData: null,
  });

  // Detect scan completion: when activeRun transitions from a real ID to null, refresh results
  const prevActiveId = useRef(undefined);
  useEffect(() => {
    if (prevActiveId.current !== undefined && prevActiveId.current !== null && !activeRun?.id) {
      refetch();
      message.success('Scan complete — results updated.');
    }
    prevActiveId.current = activeRun?.id ?? null;
  }, [activeRun?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // useMutation in @tanstack/react-query v5 uses isPending (not isLoading)
  const triggerMutation = useMutation({
    mutationFn: triggerScan,
    onSuccess: () => {
      // Give the backend ~1.5s to create the run record, then start polling
      setTimeout(refetchActive, 1500);
    },
    onError: (err) => {
      if (err.response?.status === 429) {
        const detail = err.response.data?.detail;
        const secs = detail?.retryAfterSeconds ?? 1800;
        message.warning(`Scan triggered recently — please wait ${Math.ceil(secs / 60)} more minutes.`);
      } else {
        message.error('Failed to trigger scan.');
      }
    },
  });

  const scanRun = data?.scan_run;
  const cooling = isCoolingDown(scanRun?.started_at);
  const isScanning = !!activeRun?.id;
  const scanPct = activeRun?.total_expected > 0
    ? Math.round((activeRun.scanned_so_far / activeRun.total_expected) * 100)
    : 0;

  const columns = [
    {
      title: 'Customer',
      dataIndex: 'tenant_name',
      sorter: (a, b) => a.tenant_name.localeCompare(b.tenant_name),
    },
    {
      title: 'Licence',
      dataIndex: 'licence_status',
      render: (v) => <Tag>{v}</Tag>,
    },
    {
      title: 'MFA',
      dataIndex: 'mfa_enforced',
      render: (v) => <Control value={v} />,
    },
    {
      title: 'MDM',
      dataIndex: 'mdm_configured',
      render: (v) => <Control value={v} />,
    },
    {
      title: 'Encryption',
      dataIndex: 'drive_encryption_pct',
      render: (_v, record) => <EncryptionControl record={record} />,
    },
    {
      title: 'Defender AV',
      dataIndex: 'defender_active',
      render: (v) => <Control value={v} />,
    },
    {
      title: 'Sophos AV',
      dataIndex: 'sophos_av_health_pct',
      render: (v) => <PercentControl value={v} threshold={80} />,
    },
    {
      title: 'Firewall',
      dataIndex: 'sophos_firewall_active',
      render: (v) => <Control value={v} />,
    },
    {
      title: 'Device Compliance',
      dataIndex: 'patch_compliance',
      render: (v) => <Control value={v} />,
    },
    {
      title: 'Cond. Access',
      dataIndex: 'ca_policies_present',
      key: 'ca_policies_present',
      render: (value) => <Control value={value} />,
    },
    {
      title: 'Gaps',
      dataIndex: 'gap_count',
      sorter: (a, b) => a.gap_count - b.gap_count,
      render: (count, row) =>
        count > 0 ? (
          <Popover
            content={
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {parseArray(row.gap_detail).map((g, i) => (
                  <li key={`${i}-${g}`}>{g}</li>
                ))}
              </ul>
            }
            title="Gaps to remediate"
          >
            <Tag color="warning" style={{ cursor: 'pointer' }}>
              {count} gap{count !== 1 ? 's' : ''}
            </Tag>
          </Popover>
        ) : (
          <Tag color="success">None</Tag>
        ),
    },
    {
      title: 'Status',
      dataIndex: 'overall_status',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => {
        const order = { 'AT RISK': 0, 'PARTIAL': 1, 'CE READY': 2, 'SCAN_ERROR': 3 };
        return (order[a.overall_status] ?? 9) - (order[b.overall_status] ?? 9);
      },
      render: (v, row) => (
        <Space>
          <StatusBadge status={v} />
          {row.upsell_flag && (
            <Tooltip title="Upsell opportunity — no premium licence">
              <BellOutlined style={{ color: 'var(--odin-warning)' }} />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  async function downloadFile(url, filename) {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(url, { headers });
      if (!response.ok) {
        message.error(`Export failed (${response.status}).`);
        return;
      }
      const blob = await response.blob();
      const href = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = href;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } catch (err) {
      message.error('Export failed. Check your connection.');
    }
  }

  const handleDownloadCsv = () => downloadFile(`${AGENT_URL}/ce-scan/export/csv`, 'ce-readiness-report.csv');
  const handleDownloadPdf = () => downloadFile(`${AGENT_URL}/ce-scan/export/pdf`, 'ce-readiness-report.pdf');

  if (isPending) {
    return (
      <Spin
        tip="Loading CE readiness data..."
        style={{ display: 'block', margin: '40px auto' }}
      />
    );
  }

  if (isError) {
    const is404 = error?.response?.status === 404;
    return (
      <div style={{ padding: '24px' }}>
        {isScanning && (
          <Alert
            type="info"
            style={{ marginBottom: 16 }}
            message={
              <Space direction="vertical" style={{ width: '100%' }} size={4}>
                <Space>
                  <SyncOutlined spin />
                  <Text strong>Scan in progress</Text>
                  {activeRun?.total_expected > 0 && (
                    <Text type="secondary">
                      {activeRun.scanned_so_far} / {activeRun.total_expected} tenants
                    </Text>
                  )}
                </Space>
                <Progress
                  percent={scanPct}
                  status="active"
                  size="small"
                  strokeColor="var(--odin-accent)"
                  showInfo={activeRun?.total_expected > 0}
                />
              </Space>
            }
          />
        )}
        <Alert
          type={is404 ? 'info' : 'error'}
          message={is404 ? 'No scan results yet' : 'Failed to load CE readiness data'}
          description={
            is404
              ? isAdmin
                ? 'No completed scans found. Click "Refresh Now" to run the first scan.'
                : 'No completed scans found. An administrator can trigger a manual scan, or wait for the nightly scan at 02:00 UTC.'
              : error?.message
          }
          action={
            isAdmin && (
              <Button
                type="primary"
                loading={triggerMutation.isPending}
                onClick={() => triggerMutation.mutate()}
              >
                Refresh Now
              </Button>
            )
          }
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <Space
        style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}
        wrap
      >
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Cyber Essentials Readiness Gap Report
          </Title>
          {scanRun && (
            <Text type="secondary">
              Last updated:{' '}
              {new Date(scanRun.completed_at || scanRun.started_at).toLocaleString()}
            </Text>
          )}
        </div>
        {isAdmin && (
          <Tooltip title={cooling ? 'Scan triggered recently — please wait' : 'Run scan now'}>
            <Button
              icon={<SyncOutlined />}
              loading={triggerMutation.isPending}
              disabled={cooling}
              onClick={() => triggerMutation.mutate()}
            >
              Refresh Now
            </Button>
          </Tooltip>
        )}
      </Space>

      {/* Scan progress bar — visible while a scan is running */}
      {isScanning && (
        <Alert
          type="info"
          style={{ marginBottom: 16 }}
          message={
            <Space direction="vertical" style={{ width: '100%' }} size={4}>
              <Space>
                <SyncOutlined spin />
                <Text strong>Scan in progress</Text>
                {activeRun?.total_expected > 0 && (
                  <Text type="secondary">
                    {activeRun.scanned_so_far} / {activeRun.total_expected} tenants
                  </Text>
                )}
              </Space>
              <Progress
                percent={scanPct}
                status="active"
                size="small"
                strokeColor="var(--odin-accent)"
                showInfo={activeRun.total_expected > 0}
              />
            </Space>
          }
        />
      )}

      {/* Summary banner */}
      {scanRun && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          {[
            { label: 'AT RISK', value: scanRun.at_risk_count, color: 'var(--odin-error)' },
            { label: 'PARTIAL', value: scanRun.partial_count, color: 'var(--odin-warning)' },
            { label: 'CE READY', value: scanRun.ce_ready_count, color: 'var(--odin-success)' },
            { label: 'Total', value: scanRun.tenant_count, color: 'var(--odin-accent)' },
          ].map(({ label, value, color }) => (
            <Col span={6} key={label}>
              <Card size="small">
                <Statistic title={label} value={value ?? 0} valueStyle={{ color }} />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Filters + export */}
      <Space style={{ marginBottom: 12 }} wrap>
        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          style={{ width: 160 }}
          options={[
            { value: 'ALL', label: 'All Statuses' },
            { value: 'AT RISK', label: 'AT RISK' },
            { value: 'PARTIAL', label: 'PARTIAL' },
            { value: 'CE READY', label: 'CE READY' },
          ]}
        />
        <Button
          type={upsellOnly ? 'primary' : 'default'}
          onClick={() => setUpsellOnly((v) => !v)}
        >
          {upsellOnly ? 'Upsell Only (on)' : 'Upsell Only'}
        </Button>
        <Button icon={<DownloadOutlined />} onClick={handleDownloadCsv}>
          Export CSV
        </Button>
        <Button icon={<FilePdfOutlined />} onClick={handleDownloadPdf}>
          Export PDF
        </Button>
      </Space>

      {/* Results table */}
      <Table
        dataSource={data?.results || []}
        columns={columns}
        rowKey={(record) => record.tenant_id || record.tenant_name || String(Math.random())}
        size="small"
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />
    </div>
  );
}
